import { fetchWithChatFeedback } from '../../api/chatFeedbackRequired';
/**
 * Thin fetch-based client for the /api/home-chat/* endpoints.
 *
 * All calls go through the private `apiFetch` seam (implplan §0.4a):
 *   - prefix the REACT_APP_API_URL base (or empty for same-origin)
 *   - ALWAYS pass `credentials: 'include'` for cookie-auth (spec §8a)
 *
 * On non-2xx the function throws `new Error(<response text>)`.
 *
 * Mirrors the pattern used by `ui-poc/src/components/FarChat/api.ts`.
 */

import type {
  Attachment,
  SessionListResponse,
  HomeThreadResponse,
  TurnResponse,
  HomeChatCard,
  HomeChatNavigationInstruction,
  HomeChatCardResponse,
  WorkflowRecommendationType,
  WorkflowRecommendationEntryMode,
  HomeChatPageContext,
  CreatedHomeChatWorkflow,
} from './types';
import {
  normalizeHomeChatCards,
  normalizeCreatedWorkflow,
  normalizeHomeChatLlmResultEvents,
  normalizeConstrainedSessionProjection,
  normalizeSessionActivity,
  normalizeSessionCompletion,
} from './types';
import type { HomeChatLlmResultEvent } from './types';
import type {
  Turn,
  ClarificationEnvelope,
  TopicClarificationEnvelope,
  FarRegime,
} from '../FarChat/types';
import {
  FastAnswerRecoveryError,
  isFastAnswerRecoveryEnvelope,
} from '../FarChat/types';
import {
  normalizeV2Turn,
  normalizeTopicClarificationEnvelope,
} from '../FarChat/api';
import type { RawV2Turn, PostV2TurnOptions } from '../FarChat/api';
import { extractGpcRouterResponse } from './gpcHandoff';
import type { GpcRouterResponse } from './gpcHandoff';
import { extractMrRouterResponse } from './mrHandoff';
import { extractConstructionSowRouterResponse } from './constructionSowHandoff';
import { extractServicesPwsRouterResponse } from './servicesPwsHandoff';
import type {
  MrChatSessionView,
  MrChatTurnInput,
} from '../MrChat/types';
import type { MarketResearchNotificationRequestResult } from '../../api/workflowNotifications';
import type {
  ConstructionSowArtifactRole,
  ConstructionSowChatSessionView,
  ConstructionSowChatTurnInput,
} from '../ConstructionSowChat/types';
import type {
  ServicesPwsArtifactRole,
  ServicesPwsChatSessionView,
  ServicesPwsChatTurnInput,
} from '../ServicesPwsChat/types';

const BASE_URL: string = process.env.REACT_APP_API_URL || '';

export type CompanionDocumentKind = 'construction_review' | 'construction_generated' | 'services_review' | 'services_generated';
export type CompanionRouteRequest = {
  flow_id: string; session_id?: string | null; message: string; client_request_id: string;
  editor_context?: { registration_id: string; document_kind: CompanionDocumentKind };
  explicit_intent?: 'edit' | 'question';
  regime?: FarRegime; final_question?: string; focus_token?: string; topic_focus?: string; prior_clarification_questions?: string[]; answer_mode?: string;
};
export type CompanionRouteResponse =
  | { kind: 'edit_current_document'; correlation_id: string; message: string; editor_registration_id: string }
  | { kind: 'clarify_document_intent'; correlation_id: string; message: string; editor_registration_id: string | null; choices: Array<'edit' | 'question'> }
  | { kind: 'edit_unavailable'; message: string }
  | { kind: 'conversation'; session_id: string; response: unknown };

/** Classifies and dispatches a companion turn once; edit outcomes never write DOCX. */
export async function postCompanionRoute(request: CompanionRouteRequest): Promise<CompanionRouteResponse> {
  const response = await apiFetch('/api/home-chat/companion/route', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(request),
  });
  await assertOk(response);
  const body = await response.json() as CompanionRouteResponse;
  if (!['edit_current_document', 'clarify_document_intent', 'edit_unavailable', 'conversation'].includes(body.kind)) throw new Error('The assistant response was unavailable. Please try again.');
  if (body.kind === 'conversation' && body.response && typeof body.response === 'object') {
    const raw = body.response as Record<string, unknown>;
    if (isFastAnswerRecoveryEnvelope(raw)) {
      throw new FastAnswerRecoveryError(raw);
    }
    const controller = extractGpcRouterResponse(raw) || extractMrRouterResponse(raw) || extractConstructionSowRouterResponse(raw) || extractServicesPwsRouterResponse(raw);
    if (controller) body.response = withResponseLlmResultEvents(raw, controller);
    else if (raw.turn && typeof raw.turn === 'object') body.response = { ...raw, turn: normalizeV2Turn(raw.turn as RawV2Turn) };
    else if (raw.turn_id) body.response = normalizeV2Turn(raw as unknown as RawV2Turn);
  }
  return body;
}

/** Controller views retain only the durable result ids needed by the main
 * Home Chat dispatcher.  The child controllers ignore this additive field. */
type ViewWithLlmResultEvents<T> = T & {
  llm_result_events?: HomeChatLlmResultEvent[];
};

function withLlmResultEvents<T extends object>(raw: Record<string, unknown>, view: T): ViewWithLlmResultEvents<T> {
  const events = normalizeHomeChatLlmResultEvents(raw.llm_result_events);
  return events.length ? { ...view, llm_result_events: events } : view;
}

function withResponseLlmResultEvents<T extends object>(raw: unknown, response: T): ViewWithLlmResultEvents<T> {
  return withLlmResultEvents(
    raw && typeof raw === 'object' ? raw as Record<string, unknown> : {},
    response,
  );
}

export class HomeChatHttpError extends Error {
  status: number;
  body?: unknown;
  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = 'HomeChatHttpError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Single seam where credentials policy is applied. Cookies are sent via
 * `credentials: 'include'`; no Authorization header is constructed here.
 */
async function apiFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const headers: Record<string, string> = {
    ...((init.headers as Record<string, string> | undefined) || {}),
  };
  // Forward `signal` only when the caller explicitly provided one — otherwise
  // omit the key entirely so existing callers don't observe a behavior change.
  const finalInit: RequestInit = {
    ...init,
    headers,
    credentials: 'include',
  };
  if (init.signal !== undefined) {
    finalInit.signal = init.signal;
  } else {
    delete (finalInit as { signal?: AbortSignal }).signal;
  }
  const response = await fetchWithChatFeedback(`${BASE_URL}${input}`, finalInit);
  if (response.status === 401) {
    window.dispatchEvent(new Event('auth-expired'));
  }
  return response;
}

async function assertOk(resp: Response): Promise<void> {
  if (resp.ok) return;
  let text = '';
  try {
    text = await resp.text();
  } catch {
    text = `HTTP ${resp.status}`;
  }
  // FastAPI returns its user-safe error envelope in `detail`.  Keep a
  // non-JSON response intact (for proxies and legacy endpoints), but do not
  // render the JSON wrapper itself in Home Chat.
  try {
    const payload = JSON.parse(text) as { detail?: unknown; error?: unknown; message?: unknown };
    const detail = payload.detail;
    const detailMessage = detail && typeof detail === 'object'
      ? (detail as { error?: unknown; message?: unknown }).error ?? (detail as { message?: unknown }).message
      : detail;
    const message = detailMessage ?? payload.error ?? payload.message;
    if (typeof message === 'string' && message.trim()) {
      throw new HomeChatHttpError(message, resp.status, payload);
    }
    // Lifecycle conflicts deliberately carry only a machine-readable code
    // (for example `takeover_required`). Retain that parsed envelope so the
    // MR panel can make its one safe, owner-bound recovery decision without
    // parsing an error string or exposing a takeover action.
    throw new HomeChatHttpError(`HTTP ${resp.status}`, resp.status, payload);
  } catch (cause) {
    if (cause instanceof HomeChatHttpError) throw cause;
  }
  throw new HomeChatHttpError(
    text || `HTTP ${resp.status}`,
    resp.status,
    text,
  );
}

function sessionUrl(sessionId: string, suffix: string = ''): string {
  return `/api/home-chat/sessions/${encodeURIComponent(sessionId)}${suffix}`;
}

function similarRequirementsCardUrl(sessionId: string, cardId: string, action: string): string {
  return `${sessionUrl(sessionId, `/similar-requirements/cards/${encodeURIComponent(cardId)}/${action}`)}`;
}

function similarRequirementsOpenUrl(sessionId: string): string {
  return sessionUrl(sessionId, '/similar-requirements/open');
}

const WORKFLOW_RECOMMENDATION_TYPES = new Set<WorkflowRecommendationType>([
  'market_research', 'clause_logic', 'sow_verification', 'wage_determination',
  'pnm', 'fopr', 'services', 'one_time_construction', 'services_combo', 'gpc',
  'complex_services', 'post_award_actions', 'other_transactions',
]);

const WORKFLOW_RECOMMENDATION_ENTRY_MODES = new Set<WorkflowRecommendationEntryMode>([
  'standalone_price_estimator',
]);

/** A fixed, persisted direct reply that is rendered as ordinary chat text. */
export type DirectPolicyResponse = {
  status:
    | 'classified_cui_upload'
    | 'non_classified_upload'
    | 'post_award_actions_unavailable'
    | 'construction_price_estimation_unavailable'
    | 'other_transactions_unavailable';
  session_id: string;
};

/** @deprecated Use DirectPolicyResponse for direct persisted responses. */
export type ClassifiedCuiUploadResponse = DirectPolicyResponse;

export function extractDirectPolicyResponse(
  raw: unknown
): DirectPolicyResponse | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as { status?: unknown; session_id?: unknown };
  return (
    value.status === 'classified_cui_upload'
    || value.status === 'non_classified_upload'
    || value.status === 'post_award_actions_unavailable'
    || value.status === 'construction_price_estimation_unavailable'
    || value.status === 'other_transactions_unavailable'
  )
    && typeof value.session_id === 'string'
    ? { status: value.status, session_id: value.session_id }
    : null;
}

/** @deprecated Use extractDirectPolicyResponse for direct persisted responses. */
export const extractClassifiedCuiUploadResponse = extractDirectPolicyResponse;

function unwrapCard(body: unknown): HomeChatCard {
  const card = body && typeof body === 'object' && 'chat_card' in body
    ? (body as { chat_card: unknown }).chat_card
    : body;
  const normalized = normalizeHomeChatCards([card]);
  if (normalized.length !== 1) {
    throw new Error('The similar requirements response was unavailable. Please try again.');
  }
  return normalized[0];
}

/** Parse a server-routed similar-search card before it reaches Q&A/GPC parsing. */
export function extractHomeChatCardResponse(raw: unknown): HomeChatCardResponse | null {
  if (!raw || typeof raw !== 'object' || !('chat_card' in raw)) return null;
  try {
    const chat_card = unwrapCard(raw);
    const session_id = typeof (raw as { session_id?: unknown }).session_id === 'string'
      ? (raw as unknown as { session_id: string }).session_id
      : undefined;
    return { chat_card, session_id };
  } catch {
    return null;
  }
}

export async function listSessions(): Promise<SessionListResponse> {
  const resp = await apiFetch('/api/home-chat/sessions', { method: 'GET' });
  await assertOk(resp);
  const body = await resp.json() as SessionListResponse;
  if (!Array.isArray(body.sessions)) return body;
  return {
    ...body,
    sessions: body.sessions.map((session) => {
      if (!session || typeof session !== 'object') return session;
      const {
        activity: rawActivity,
        completion: rawCompletion,
        llm_result_events: rawLlmResultEvents,
        constrained_session: rawConstrainedSession,
        ...withoutActivity
      } = session;
      const activity = normalizeSessionActivity(rawActivity);
      const completion = normalizeSessionCompletion(rawCompletion);
      const llmResultEvents = normalizeHomeChatLlmResultEvents(rawLlmResultEvents);
      const constrainedSession = normalizeConstrainedSessionProjection(rawConstrainedSession);
      return {
        ...withoutActivity,
        ...(activity ? { activity } : {}),
        ...(completion ? { completion } : {}),
        ...(llmResultEvents.length ? { llm_result_events: llmResultEvents } : {}),
        ...(constrainedSession ? { constrained_session: constrainedSession } : {}),
      };
    }),
  };
}

export type HomeChatOperationCancellation = {
  operation_id: string;
  state: 'stopping' | 'cancelled';
};

/** Request server-authoritative, cooperative cancellation. This never aborts the browser turn request. */
export async function cancelHomeChatOperation(
  sessionId: string,
  operationId: string,
): Promise<HomeChatOperationCancellation> {
  const resp = await apiFetch(
    sessionUrl(sessionId, `/operations/${encodeURIComponent(operationId)}/cancel`),
    { method: 'POST' },
  );
  await assertOk(resp);
  return (await resp.json()) as HomeChatOperationCancellation;
}

/** Atomically mark one owner-visible completion as seen after the user opens it. */
export async function acknowledgeCompletion(
  sessionId: string,
  completionId: string,
): Promise<{ completion_id: string; acknowledged: boolean }> {
  const resp = await apiFetch(
    sessionUrl(sessionId, `/completions/${encodeURIComponent(completionId)}/acknowledge`),
    { method: 'POST' },
  );
  await assertOk(resp);
  return (await resp.json()) as { completion_id: string; acknowledged: boolean };
}

export type ReplaceHomeChatTurnRequest = {
  message: string;
  client_request_id: string;
  expected_branch_revision: number;
};

/** Server-authorized replacement branch for one eligible ordinary v2 turn. */
export async function replaceHomeChatTurn(
  sessionId: string,
  turnId: string,
  body: ReplaceHomeChatTurnRequest,
): Promise<unknown> {
  const resp = await apiFetch(
    sessionUrl(sessionId, `/turns/${encodeURIComponent(turnId)}/replace`),
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
  );
  await assertOk(resp);
  return resp.json();
}

/**
 * Eagerly allocate a server-side session (used when the user clicks
 * "New chat" or drops a file into a fresh panel). Returns the new
 * `session_id` so the caller can adopt it as the active session before
 * any turn is posted.
 */
export async function createSession(): Promise<{ session_id: string }> {
  const resp = await apiFetch('/api/home-chat/sessions', { method: 'POST' });
  await assertOk(resp);
  return (await resp.json()) as { session_id: string };
}

/**
 * Start one fixed, server-authorized workflow from a constrained Home Chat
 * shell. This never routes free text through the general v2 Q&A endpoint.
 */
export async function startConstrainedWorkflowEntry(
  sessionId: string,
  workflow: 'market_research' | 'gpc',
  requirementHint: string,
  clientRequestId: string,
): Promise<unknown> {
  const resp = await apiFetch(sessionUrl(sessionId, '/workflow-entry'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      workflow,
      requirement_hint: requirementHint,
      client_request_id: clientRequestId,
    }),
  });
  await assertOk(resp);
  return resp.json();
}

/**
 * Route free-text only through the RA's constrained MR/GPC intake seam.
 * This is intentionally distinct from both fixed workflow-entry selection
 * and the general `/v2/turn` FAR-chat endpoint.
 */
export async function routeConstrainedWorkflowTypedEntry(
  sessionId: string,
  message: string,
  clientRequestId: string,
): Promise<unknown> {
  const resp = await apiFetch(sessionUrl(sessionId, '/workflow-entry/route'), {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, client_request_id: clientRequestId }),
  });
  await assertOk(resp);
  return resp.json();
}

/** Relay an opaque MR/GPC-only choice issued by the constrained intake seam. */
export async function selectConstrainedWorkflowTypedEntryChoice(
  sessionId: string,
  cardId: string,
  selectionKey: string,
  clientRequestId: string,
): Promise<unknown> {
  const resp = await apiFetch(sessionUrl(sessionId, '/workflow-entry/choice'), {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      card_id: cardId, selection_key: selectionKey,
      client_request_id: clientRequestId,
    }),
  });
  await assertOk(resp);
  return resp.json();
}

/** Claim a server-issued purchase guidance option; the key carries no client route. */
export async function selectPurchaseGuidance(
  sessionId: string,
  cardId: string,
  selectionKey: string,
  clientRequestId: string,
): Promise<unknown> {
  const resp = await apiFetch(sessionUrl(sessionId, `/purchase-guidance/${encodeURIComponent(cardId)}/select`), {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ selection_key: selectionKey, client_request_id: clientRequestId }),
  });
  await assertOk(resp);
  return resp.json();
}

/** Answer a server-issued purchase clarification inside its existing chat session. */
export async function clarifyMissionPartnerGuidance(sessionId: string, cardId: string, reply: string, clientRequestId: string): Promise<unknown> {
  return clarifyGuidance(sessionId, cardId, reply, clientRequestId, true);
}

export async function clarifyPurchaseGuidance(sessionId: string, cardId: string, message: string, clientRequestId: string): Promise<unknown> {
  return clarifyGuidance(sessionId, cardId, message, clientRequestId, false);
}

async function clarifyGuidance(
  sessionId: string,
  cardId: string,
  message: string,
  clientRequestId: string,
  mission: boolean,
): Promise<unknown> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    // Some proxies and test fetch implementations do not settle on abort.
    // Race the complete request (including JSON parsing) so the composer can
    // never remain in its pending state indefinitely.
    return await Promise.race([
      (async () => {
        const resp = await apiFetch(sessionUrl(sessionId, `/${mission ? 'mission-partner-guidance' : 'purchase-guidance'}/${encodeURIComponent(cardId)}/clarify`), {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...(mission ? { reply: message } : { message }), client_request_id: clientRequestId }),
          signal: controller.signal,
        });
        await assertOk(resp);
        return resp.json();
      })(),
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => {
          controller.abort();
          reject(new Error('This follow-up is taking too long. You can retry it here.'));
        }, 20_000);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/** Continue a workflow-only GPC handoff without touching the general v2 route. */
export async function confirmConstrainedGpcWorkflowEntry(
  sessionId: string,
  handoffId: string,
  confirmation: 'confirmed' | 'revise',
  clientRequestId: string,
): Promise<unknown> {
  const resp = await apiFetch(sessionUrl(sessionId, '/workflow-entry/gpc/confirm'), {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      gpc_handoff_id: handoffId, confirmation, client_request_id: clientRequestId,
    }),
  });
  await assertOk(resp);
  return resp.json();
}

/** Send a bound GPC controller turn through its constrained server entry. */
export async function postConstrainedGpcWorkflowTurn(
  sessionId: string,
  payload: { client_request_id: string; input?: Record<string, unknown>; message?: string },
): Promise<unknown> {
  const resp = await apiFetch(sessionUrl(sessionId, '/workflow-entry/gpc/turn'), {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  await assertOk(resp);
  return resp.json();
}

export async function getThread(sessionId: string): Promise<HomeThreadResponse> {
  const resp = await apiFetch(sessionUrl(sessionId, '/thread'), { method: 'GET' });
  await assertOk(resp);
  const body = await resp.json() as HomeThreadResponse;
  const llmResultEvents = normalizeHomeChatLlmResultEvents(body.llm_result_events);
  const { llm_result_events: _rawLlmResultEvents, ...thread } = body;
  return {
    ...thread,
    ...(llmResultEvents.length ? { llm_result_events: llmResultEvents } : {}),
  };
}

export async function getWorkflowCompanionSession(
  flowId: string,
): Promise<string | null | undefined> {
  const resp = await apiFetch(
    `/api/home-chat/workflow-companions/${encodeURIComponent(flowId)}`,
    { method: 'GET' },
  );
  await assertOk(resp);
  const body = (await resp.json()) as {
    session_id?: unknown;
    binding_present?: unknown;
  };
  if (typeof body.session_id === 'string') return body.session_id;
  return body.binding_present === true ? null : undefined;
}

export async function setWorkflowCompanionSession(
  flowId: string,
  sessionId: string | null,
): Promise<void> {
  const resp = await apiFetch(
    `/api/home-chat/workflow-companions/${encodeURIComponent(flowId)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    },
  );
  await assertOk(resp);
}

export async function selectSimilarRequirementsType(
  sessionId: string,
  cardId: string,
  selectionKey: string,
  clientRequestId: string
): Promise<HomeChatCard> {
  const resp = await apiFetch(similarRequirementsCardUrl(sessionId, cardId, 'select-type'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ selection_key: selectionKey, client_request_id: clientRequestId }),
  });
  await assertOk(resp);
  return unwrapCard(await resp.json());
}

export async function selectTrainingAction(
  sessionId: string, cardId: string,
): Promise<void> {
  const resp = await apiFetch(
    `${sessionUrl(sessionId)}/training-actions/${encodeURIComponent(cardId)}/select`,
    { method: 'POST' },
  );
  await assertOk(resp);
  const body = await resp.json();
  if (body?.status !== 'training_navigation' || body?.destination !== 'training') {
    throw new Error('Training navigation was unavailable.');
  }
}

export async function selectHomeChatIntentChoice(
  sessionId: string,
  cardId: string,
  selectionKey: string,
  clientRequestId: string
): Promise<unknown> {
  const resp = await apiFetch(`${sessionUrl(sessionId, `/intent-choices/${encodeURIComponent(cardId)}/select`)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ selection_key: selectionKey, client_request_id: clientRequestId }),
  });
  if (!resp.ok) {
    let body: unknown;
    let text = '';
    try {
      body = await resp.json();
      text = typeof body === 'string' ? body : JSON.stringify(body);
    } catch {
      try {
        text = await resp.text();
      } catch {
        text = '';
      }
    }
    throw new HomeChatHttpError(
      `HTTP ${resp.status} ${text}`.trim(),
      resp.status,
      body
    );
  }
  return await resp.json();
}

/** Claim one opaque recommendation key and receive the App handoff plus safe receipt. */
export async function selectWorkflowRecommendation(
  sessionId: string,
  cardId: string,
  selectionKey: string,
  clientRequestId: string
): Promise<{
  workflow_type: WorkflowRecommendationType;
  entry_mode?: WorkflowRecommendationEntryMode;
  selected_label: string;
  workflow_handoff_receipt: string;
  created_workflow?: CreatedHomeChatWorkflow;
}> {
  const resp = await apiFetch(
    sessionUrl(sessionId, `/workflow-recommendations/${encodeURIComponent(cardId)}/select`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ selection_key: selectionKey, client_request_id: clientRequestId }),
    }
  );
  if (!resp.ok) throw new Error('That choice is no longer available.');
  const body = await resp.json() as {
    workflow_type?: unknown;
    entry_mode?: unknown;
    selected_label?: unknown;
    workflow_handoff_receipt?: unknown;
    created_workflow?: unknown;
  };
  if (
    typeof body?.workflow_type !== 'string'
    || !WORKFLOW_RECOMMENDATION_TYPES.has(body.workflow_type as WorkflowRecommendationType)
    || (body.entry_mode !== undefined
      && (typeof body.entry_mode !== 'string'
        || !WORKFLOW_RECOMMENDATION_ENTRY_MODES.has(
          body.entry_mode as WorkflowRecommendationEntryMode,
        )))
    || typeof body.selected_label !== 'string'
    || !body.selected_label.trim()
    || typeof body.workflow_handoff_receipt !== 'string'
    || !body.workflow_handoff_receipt.trim()
  ) {
    throw new Error('That choice is no longer available.');
  }
  const createdWorkflow = body.created_workflow === undefined ? undefined : normalizeCreatedWorkflow(body.created_workflow);
  if (createdWorkflow === null) throw new Error('That choice is no longer available.');
  return {
    workflow_type: body.workflow_type as WorkflowRecommendationType,
    ...(typeof body.entry_mode === 'string'
      ? { entry_mode: body.entry_mode as WorkflowRecommendationEntryMode }
      : {}),
    selected_label: body.selected_label,
    workflow_handoff_receipt: body.workflow_handoff_receipt,
    ...(createdWorkflow ? { created_workflow: createdWorkflow } : {}),
  };
}

/** Relay one opaque generic choice key; the server owns its dispatch map. */
export async function selectHomeChatActionChoice(
  sessionId: string, cardId: string, selectionKey: string, clientRequestId: string,
): Promise<unknown> {
  const resp = await apiFetch(sessionUrl(sessionId, `/action-choices/${encodeURIComponent(cardId)}/select`), {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ selection_key: selectionKey, client_request_id: clientRequestId }),
  });
  await assertOk(resp);
  return await resp.json();
}

/** Send a turn to the server-owned MR controller bound to this Home Chat thread. */
export async function postMrChatTurn(
  sessionId: string,
  mrChatSessionId: string,
  input: MrChatTurnInput,
): Promise<ViewWithLlmResultEvents<MrChatSessionView>> {
  const resp = await apiFetch(sessionUrl(sessionId, '/mr-chat/turn'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mr_chat_session_id: mrChatSessionId, ...input }),
  });
  await assertOk(resp);
  const raw = await resp.json() as Record<string, unknown>;
  const view = raw.mr_chat && typeof raw.mr_chat === 'object' ? raw.mr_chat : raw;
  if (!view || typeof (view as { session_id?: unknown }).session_id !== 'string') {
    throw new Error('Unable to update Market Research. Please try again.');
  }
  return withLlmResultEvents(raw, extractMrRouterResponse(raw) || view as MrChatSessionView);
}

/**
 * Relay the controller-issued Section C handoff from its owned Home Chat
 * binding.  The browser never supplies a flow, recipient, stage, or action
 * identifier; the server rechecks all of those from the current controller.
 */
export async function postMrChatSectionCHandoff(
  sessionId: string,
  clientRequestId: string,
): Promise<ViewWithLlmResultEvents<MrChatSessionView>> {
  const resp = await apiFetch(sessionUrl(sessionId, '/mr-chat/section-c-handoff'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ client_request_id: clientRequestId }),
  });
  await assertOk(resp);
  const raw = await resp.json() as Record<string, unknown>;
  const view = raw.mr_chat && typeof raw.mr_chat === 'object' ? raw.mr_chat : raw;
  if (!view || typeof (view as { session_id?: unknown }).session_id !== 'string') {
    throw new Error('Unable to hand off Section C. Please try again.');
  }
  return withLlmResultEvents(raw, extractMrRouterResponse(raw) || view as MrChatSessionView);
}

/**
 * Create the Questions request from the authenticated Home Chat MR binding.
 * The request carries no flow or controller/source information.
 */
export async function postMrChatQuestionsNotification(
  sessionId: string,
  clientRequestId: string,
): Promise<MarketResearchNotificationRequestResult> {
  const resp = await apiFetch(sessionUrl(sessionId, '/mr-chat/questions-notify'), {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ client_request_id: clientRequestId }),
  });
  await assertOk(resp);
  const raw = await resp.json() as { data?: unknown };
  if (!raw.data || typeof raw.data !== 'object') throw new Error('Unable to create the notification. Please try again.');
  return raw.data as MarketResearchNotificationRequestResult;
}

/** Bound counterpart share + Questions handoff; flow/source remain server-owned. */
export async function postMrChatQuestionsShareAndNotify(
  sessionId: string,
  emails: string[],
  clientRequestId: string,
): Promise<MarketResearchNotificationRequestResult> {
  const resp = await apiFetch(sessionUrl(sessionId, '/mr-chat/questions-share-and-notify'), {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ emails, client_request_id: clientRequestId }),
  });
  await assertOk(resp);
  const raw = await resp.json() as { data?: unknown };
  if (!raw.data || typeof raw.data !== 'object') throw new Error('Unable to share and create the notification. Please try again.');
  return raw.data as MarketResearchNotificationRequestResult;
}

/** Hydrate an existing, owner-bound MR controller without creating a new flow. */
export async function getBoundMrChat(sessionId: string): Promise<ViewWithLlmResultEvents<MrChatSessionView>> {
  const resp = await apiFetch(sessionUrl(sessionId, '/mr-chat/start'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  await assertOk(resp);
  const raw = await resp.json() as Record<string, unknown>;
  const view = raw.mr_chat && typeof raw.mr_chat === 'object' ? raw.mr_chat : raw;
  if (!view || typeof (view as { session_id?: unknown }).session_id !== 'string') {
    throw new Error('Unable to load Market Research. Please try again.');
  }
  return withLlmResultEvents(raw, extractMrRouterResponse(raw) || view as MrChatSessionView);
}

/** Safely rebuild the owner-bound MR projection after a typed lifecycle 409. */
export async function refreshBoundMrChat(sessionId: string): Promise<ViewWithLlmResultEvents<MrChatSessionView>> {
  const resp = await apiFetch(sessionUrl(sessionId, '/mr-chat/refresh'), {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
  });
  await assertOk(resp);
  const raw = await resp.json() as Record<string, unknown>;
  const view = raw.mr_chat && typeof raw.mr_chat === 'object' ? raw.mr_chat : raw;
  if (!view || typeof (view as { session_id?: unknown }).session_id !== 'string') {
    throw new Error('Unable to refresh Market Research. Please try again.');
  }
  return withLlmResultEvents(raw, extractMrRouterResponse(raw) || view as MrChatSessionView);
}

/** Poll the owner-bound MR controller view; this is not the generic v2 turn-progress API. */
export async function getMrChatProgress(sessionId: string, poll = 0): Promise<ViewWithLlmResultEvents<MrChatSessionView>> {
  const resp = await apiFetch(sessionUrl(sessionId, `/mr-chat/progress?poll=${encodeURIComponent(String(poll))}`), { method: 'GET' });
  await assertOk(resp);
  const raw = await resp.json() as Record<string, unknown>;
  const data = raw.data && typeof raw.data === 'object' ? raw.data as Record<string, unknown> : raw;
  const view = data.mr_chat && typeof data.mr_chat === 'object' ? data.mr_chat : data;
  if (!view || typeof (view as { session_id?: unknown }).session_id !== 'string') {
    throw new Error('Unable to load Market Research progress. Please try again.');
  }
  return withLlmResultEvents(data, extractMrRouterResponse(data) || view as MrChatSessionView);
}

function constructionSowChatView(raw: unknown, failureMessage: string): ViewWithLlmResultEvents<ConstructionSowChatSessionView> {
  const root = raw && typeof raw === 'object' ? raw as Record<string, unknown> : null;
  const view = root?.construction_sow_chat && typeof root.construction_sow_chat === 'object'
    ? root.construction_sow_chat : root;
  if (!view || typeof (view as { session_id?: unknown }).session_id !== 'string') throw new Error(failureMessage);
  return withLlmResultEvents(root || {}, view as ConstructionSowChatSessionView);
}

function servicesPwsChatView(raw: unknown, failureMessage: string): ViewWithLlmResultEvents<ServicesPwsChatSessionView> {
  const root = raw && typeof raw === 'object' ? raw as Record<string, unknown> : null;
  const view = root?.services_pws_chat && typeof root.services_pws_chat === 'object'
    ? root.services_pws_chat : root;
  if (!view || typeof (view as { session_id?: unknown }).session_id !== 'string') throw new Error(failureMessage);
  return withLlmResultEvents(root || {}, view as ServicesPwsChatSessionView);
}

export async function postServicesPwsChatTurn(homeChatSessionId: string, input: ServicesPwsChatTurnInput): Promise<ServicesPwsChatSessionView> {
  const resp = await apiFetch(sessionUrl(homeChatSessionId, '/services-pws-chat/turn'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) });
  await assertOk(resp);
  return servicesPwsChatView(await resp.json(), 'Unable to update Services PWS. Please try again.');
}

export async function getBoundServicesPwsChat(homeChatSessionId: string, clientRequestId: string): Promise<ServicesPwsChatSessionView> {
  const resp = await apiFetch(sessionUrl(homeChatSessionId, '/services-pws-chat/start'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ client_request_id: clientRequestId }) });
  await assertOk(resp);
  return servicesPwsChatView(await resp.json(), 'Unable to load Services PWS. Please try again.');
}

export async function getServicesPwsChatProgress(homeChatSessionId: string, poll = 0): Promise<ServicesPwsChatSessionView> {
  const resp = await apiFetch(sessionUrl(homeChatSessionId, `/services-pws-chat/progress?poll=${encodeURIComponent(String(poll))}`), { method: 'GET' });
  await assertOk(resp);
  return servicesPwsChatView(await resp.json(), 'Unable to load Services PWS progress. Please try again.');
}

export async function uploadServicesPwsRequirementDocument(homeChatSessionId: string, file: File, clientRequestId: string): Promise<ServicesPwsChatSessionView> {
  const form = new FormData(); form.append('file', file); form.append('client_request_id', clientRequestId);
  const resp = await apiFetch(sessionUrl(homeChatSessionId, '/services-pws-chat/requirement-document'), { method: 'POST', body: form });
  await assertOk(resp); return servicesPwsChatView(await resp.json(), 'Unable to upload the requirement document. Please try again.');
}

export async function extractServicesPwsRequirementDocument(homeChatSessionId: string, confirmOverwrite: boolean, clientRequestId: string): Promise<ServicesPwsChatSessionView> {
  const resp = await apiFetch(sessionUrl(homeChatSessionId, '/services-pws-chat/requirement-document/extract'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ confirm_overwrite: confirmOverwrite, client_request_id: clientRequestId }) });
  await assertOk(resp); return servicesPwsChatView(await resp.json(), 'Unable to extract the requirement document. Please try again.');
}

export async function removeServicesPwsRequirementDocument(homeChatSessionId: string, clientRequestId: string): Promise<ServicesPwsChatSessionView> {
  const resp = await apiFetch(sessionUrl(homeChatSessionId, `/services-pws-chat/requirement-document?client_request_id=${encodeURIComponent(clientRequestId)}`), { method: 'DELETE' });
  await assertOk(resp); return servicesPwsChatView(await resp.json(), 'Unable to remove the requirement document. Please try again.');
}

export async function uploadServicesPwsSupportDocument(homeChatSessionId: string, role: Exclude<ServicesPwsArtifactRole, 'requirements'>, file: File, clientRequestId: string): Promise<ServicesPwsChatSessionView> {
  const form = new FormData(); form.append('file', file); form.append('role', role); form.append('client_request_id', clientRequestId);
  const resp = await apiFetch(sessionUrl(homeChatSessionId, '/services-pws-chat/support-documents'), { method: 'POST', body: form });
  await assertOk(resp); return servicesPwsChatView(await resp.json(), 'Unable to upload the supporting document. Please try again.');
}

export async function replaceServicesPwsSupportDocument(homeChatSessionId: string, artifactId: string, role: Exclude<ServicesPwsArtifactRole, 'requirements'>, file: File, clientRequestId: string): Promise<ServicesPwsChatSessionView> {
  const form = new FormData(); form.append('file', file); form.append('role', role); form.append('client_request_id', clientRequestId);
  const resp = await apiFetch(sessionUrl(homeChatSessionId, `/services-pws-chat/support-documents/${encodeURIComponent(artifactId)}/replace`), { method: 'POST', body: form });
  await assertOk(resp); return servicesPwsChatView(await resp.json(), 'Unable to replace the supporting document. Please try again.');
}

export async function removeServicesPwsSupportDocument(homeChatSessionId: string, artifactId: string, clientRequestId: string): Promise<ServicesPwsChatSessionView> {
  const resp = await apiFetch(sessionUrl(homeChatSessionId, `/services-pws-chat/support-documents/${encodeURIComponent(artifactId)}?client_request_id=${encodeURIComponent(clientRequestId)}`), { method: 'DELETE' });
  await assertOk(resp); return servicesPwsChatView(await resp.json(), 'Unable to remove the supporting document. Please try again.');
}

export async function openGeneratedServicesPws(homeChatSessionId: string, clientRequestId: string): Promise<{ flow_id: string }> {
  const issued = await apiFetch(sessionUrl(homeChatSessionId, '/services-pws-chat/view-generated-pws'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ client_request_id: clientRequestId }) });
  await assertOk(issued);
  const receipt = await issued.json() as { navigation_handle?: unknown };
  if (typeof receipt.navigation_handle !== 'string' || !receipt.navigation_handle) throw new Error('This generated PWS is no longer available.');
  const redeemed = await apiFetch(sessionUrl(homeChatSessionId, '/services-pws-chat/redeem-generated-pws'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ navigation_handle: receipt.navigation_handle }) });
  await assertOk(redeemed);
  const target = await redeemed.json() as { flow_id?: unknown; workflow_type?: unknown; stage?: unknown };
  if (typeof target.flow_id !== 'string' || (target.workflow_type !== 'services' && target.workflow_type !== 'complex_services') || target.stage !== 622) throw new Error('This generated PWS is no longer available.');
  return { flow_id: target.flow_id };
}

/** Post a canonical construction chat action; the parent Home Chat binding is server-owned. */
export async function postConstructionSowChatTurn(
  homeChatSessionId: string,
  input: ConstructionSowChatTurnInput,
): Promise<ConstructionSowChatSessionView> {
  const resp = await apiFetch(sessionUrl(homeChatSessionId, '/construction-sow-chat/turn'), {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
  });
  await assertOk(resp);
  return constructionSowChatView(await resp.json(), 'Unable to update Construction SOW. Please try again.');
}

/** Hydrate (or explicitly server-start) the construction controller bound to this Home Chat. */
export async function getBoundConstructionSowChat(homeChatSessionId: string, clientRequestId: string): Promise<ConstructionSowChatSessionView> {
  const resp = await apiFetch(sessionUrl(homeChatSessionId, '/construction-sow-chat/start'), {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ client_request_id: clientRequestId }),
  });
  await assertOk(resp);
  return constructionSowChatView(await resp.json(), 'Unable to load Construction SOW. Please try again.');
}

export async function getConstructionSowChatProgress(homeChatSessionId: string, poll = 0): Promise<ConstructionSowChatSessionView> {
  const resp = await apiFetch(sessionUrl(homeChatSessionId, `/construction-sow-chat/progress?poll=${encodeURIComponent(String(poll))}`), { method: 'GET' });
  await assertOk(resp);
  return constructionSowChatView(await resp.json(), 'Unable to load Construction SOW progress. Please try again.');
}

export async function uploadConstructionSowRequirementDocument(homeChatSessionId: string, file: File, clientRequestId: string): Promise<ConstructionSowChatSessionView> {
  const form = new FormData();
  form.append('file', file);
  form.append('client_request_id', clientRequestId);
  const resp = await apiFetch(sessionUrl(homeChatSessionId, '/construction-sow-chat/requirement-document'), { method: 'POST', body: form });
  await assertOk(resp);
  return constructionSowChatView(await resp.json(), 'Unable to upload the requirements package. Please try again.');
}

/** Extract a persisted requirements package; overwrite confirmation stays explicit. */
export async function extractConstructionSowRequirementDocument(homeChatSessionId: string, confirmOverwrite: boolean, clientRequestId: string): Promise<ConstructionSowChatSessionView> {
  const resp = await apiFetch(sessionUrl(homeChatSessionId, '/construction-sow-chat/requirement-document/extract'), {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ confirm_overwrite: confirmOverwrite, client_request_id: clientRequestId }),
  });
  await assertOk(resp);
  return constructionSowChatView(await resp.json(), 'Unable to extract the requirements package. Please try again.');
}

/** Remove the single canonical requirement package while retaining server-side provenance. */
export async function removeConstructionSowRequirementDocument(homeChatSessionId: string, clientRequestId: string): Promise<ConstructionSowChatSessionView> {
  const resp = await apiFetch(sessionUrl(homeChatSessionId, `/construction-sow-chat/requirement-document?client_request_id=${encodeURIComponent(clientRequestId)}`), {
    method: 'DELETE',
  });
  await assertOk(resp);
  return constructionSowChatView(await resp.json(), 'Unable to remove the requirements package. Please try again.');
}

export async function uploadConstructionSowSupportDocument(
  homeChatSessionId: string,
  role: Exclude<ConstructionSowArtifactRole, 'requirements'>,
  file: File,
  clientRequestId: string,
): Promise<ConstructionSowChatSessionView> {
  const form = new FormData();
  form.append('file', file);
  form.append('role', role);
  form.append('client_request_id', clientRequestId);
  const resp = await apiFetch(sessionUrl(homeChatSessionId, '/construction-sow-chat/support-documents'), { method: 'POST', body: form });
  await assertOk(resp);
  return constructionSowChatView(await resp.json(), 'Unable to upload the supporting document. Please try again.');
}

/** Replace a server-owned support artifact. Its canonical role remains immutable. */
export async function replaceConstructionSowSupportDocument(
  homeChatSessionId: string,
  artifactId: string,
  file: File,
  clientRequestId: string,
): Promise<ConstructionSowChatSessionView> {
  const form = new FormData();
  form.append('file', file);
  form.append('client_request_id', clientRequestId);
  const resp = await apiFetch(sessionUrl(homeChatSessionId, `/construction-sow-chat/support-documents/${encodeURIComponent(artifactId)}/replace`), { method: 'POST', body: form });
  await assertOk(resp);
  return constructionSowChatView(await resp.json(), 'Unable to replace the supporting document. Please try again.');
}

/** Remove a server-owned support artifact; the Home Chat never sends a flow id. */
export async function removeConstructionSowSupportDocument(homeChatSessionId: string, artifactId: string, clientRequestId: string): Promise<ConstructionSowChatSessionView> {
  const resp = await apiFetch(sessionUrl(homeChatSessionId, `/construction-sow-chat/support-documents/${encodeURIComponent(artifactId)}?client_request_id=${encodeURIComponent(clientRequestId)}`), {
    method: 'DELETE',
  });
  await assertOk(resp);
  return constructionSowChatView(await resp.json(), 'Unable to remove the supporting document. Please try again.');
}

/** Issue then redeem the terminal, owner-bound construction navigation receipt. */
export async function openGeneratedConstructionSow(homeChatSessionId: string, clientRequestId: string): Promise<{ flow_id: string }> {
  const issued = await apiFetch(sessionUrl(homeChatSessionId, '/construction-sow-chat/view-generated-sow'), {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ client_request_id: clientRequestId }),
  });
  await assertOk(issued);
  const receipt = await issued.json() as { navigation_handle?: unknown };
  if (typeof receipt.navigation_handle !== 'string' || !receipt.navigation_handle) throw new Error('This generated SOW is no longer available.');
  const redeemed = await apiFetch(sessionUrl(homeChatSessionId, '/construction-sow-chat/redeem-generated-sow'), {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ navigation_handle: receipt.navigation_handle }),
  });
  await assertOk(redeemed);
  const target = await redeemed.json() as { flow_id?: unknown; workflow_type?: unknown; stage?: unknown };
  if (typeof target.flow_id !== 'string' || target.workflow_type !== 'one_time_construction' || target.stage !== 206) {
    throw new Error('This generated SOW is no longer available.');
  }
  return { flow_id: target.flow_id };
}

/**
 * Upload vendor quote PDFs through the canonical Stage-12 endpoint. The server
 * persists the files on the owned MR flow and returns opaque persisted refs;
 * Home Chat relays only those refs in its subsequent controller turn.
 */
export async function uploadMrVendorQuotes(flowId: string, files: File[]): Promise<string[]> {
  if (!flowId || files.length === 0) return [];
  const form = new FormData();
  form.append('flow_id', flowId);
  files.forEach((file) => form.append('files', file));
  const resp = await apiFetch('/api/vendor-bids/upload', { method: 'POST', body: form });
  await assertOk(resp);
  const raw = await resp.json() as { uploaded_files?: unknown };
  const refs = Array.isArray(raw.uploaded_files) ? raw.uploaded_files.flatMap((item) => {
    // The Stage-12 API owns the files and its `file_id` is the opaque,
    // canonical reference that the subsequent Home Chat turn must relay.
    // Do not substitute the display filename: it is not an ownership-safe
    // identifier and can collide with another upload.
    if (typeof item === 'string' && item) return [item];
    if (!item || typeof item !== 'object') return [];
    const record = item as { file_id?: unknown };
    const ref = record.file_id;
    return typeof ref === 'string' && ref ? [ref] : [];
  }) : [];
  if (refs.length === 0) throw new Error('Vendor quote upload did not return persisted file references.');
  return refs;
}

export async function cancelSimilarRequirementsCard(
  sessionId: string,
  cardId: string,
  clientRequestId: string
): Promise<{ card_id: string; state: 'cancelled' }> {
  const resp = await apiFetch(similarRequirementsCardUrl(sessionId, cardId, 'cancel'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ client_request_id: clientRequestId }),
  });
  await assertOk(resp);
  const body = await resp.json();
  if (!body || body.card_id !== cardId || body.state !== 'cancelled') {
    throw new Error('The similar requirements response was unavailable. Please try again.');
  }
  return { card_id: cardId, state: 'cancelled' };
}

export async function openSimilarRequirementsResult(
  sessionId: string,
  openHandle: string
): Promise<HomeChatNavigationInstruction> {
  const resp = await apiFetch(similarRequirementsOpenUrl(sessionId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ open_handle: openHandle }),
  });
  await assertOk(resp);
  const body = await resp.json();
  if (!body || typeof body.navigation_handle !== 'string' || !body.navigation_handle) {
    throw new Error('This workflow is no longer available.');
  }
  return { navigation_handle: body.navigation_handle };
}

export async function postTurn(
  sessionId: string | 'new',
  body: { message: string; client_request_id: string }
): Promise<TurnResponse & { session_id: string }> {
  const resp = await apiFetch(sessionUrl(sessionId, '/turn'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  await assertOk(resp);
  return (await resp.json()) as TurnResponse & { session_id: string };
}

/**
 * v2 turn endpoint for HomeChat (parallels FarChat's `postV2Turn`).
 *
 * POSTs to ``/api/home-chat/sessions/{sessionId}/v2/turn``. The wire shape
 * matches the FarChat v2 endpoint — Reader A + Reader B run in parallel
 * and the synthesizer produces a single ``Turn``. Throws an Error with a
 * status-tagged message on non-2xx so the UI can detect 503 and fall
 * back to v1.
 *
 * The error message includes the HTTP status as ``HTTP <status>`` and
 * the response body so the panel's existing 503 detection regex
 * (``/503|llm_unavailable|reference_data_unavailable/i``) keeps working.
 */
export interface HomeChatTurnOptions {
  gpcConfirmation?: 'confirmed' | 'far_qa' | 'revise';
  gpcChatSessionId?: string;
  gpcInput?: Record<string, string | number | boolean>;
  /** Opaque owner-bound token issued by the server's router. */
  gpcHandoffId?: string;
  /** Current workflow page for the inline companion; never authorization. */
  pageContext?: HomeChatPageContext;
}

function pageContextBody(
  context: HomeChatPageContext,
): Record<string, string | number> {
  // Flow creation accepts longer names than the conversational page-context
  // contract. Keep the useful identifying prefix instead of making every
  // companion turn fail validation for an otherwise valid workflow.
  const workflowName = context.workflowName?.slice(0, 160);
  return {
    workflow_type: context.workflowType,
    workflow_label: context.workflowLabel,
    ...(workflowName ? { workflow_name: workflowName } : {}),
    stage_name: context.stageName,
    ...(context.workflowType === 'market_research' && context.flowId && context.stageNumber
      ? { flow_id: context.flowId, stage_number: context.stageNumber }
      : {}),
    ...(context.stageDescription
      ? { stage_description: context.stageDescription }
      : {}),
  };
}

export async function postV2Turn(
  sessionId: string,
  message: string,
  clientRequestId?: string,
  finalQuestion?: string,
  regime?: FarRegime,
  options?: PostV2TurnOptions,
  turnOptions?: HomeChatTurnOptions
): Promise<
  Turn
  | ClarificationEnvelope
  | TopicClarificationEnvelope
  | GpcRouterResponse
  | MrChatSessionView
  | ConstructionSowChatSessionView
  | ServicesPwsChatSessionView
  | HomeChatCardResponse
> {
  // `final_question` is sent ONLY when resolving a clarification; its
  // presence makes the backend skip the gate. Never forward a stale one
  // (see clarification-step spec §4.1 state hygiene). `regime` carries the
  // user's chosen FAR Version on the resolve handoff (legacy-far-readers
  // spec §4.1). `focus_token` resolves a post-reader topic-focus round-trip
  // (skips Stage-1 AND the readers); likewise never leak it into a fresh
  // send (post-reader spec §4.1 / §9 state hygiene).
  const body: {
    message: string;
    client_request_id?: string;
    final_question?: string;
    regime?: FarRegime;
    focus_token?: string;
    topic_focus?: string;
    prior_clarification_questions?: string[];
    gpc_confirmation?: 'confirmed' | 'far_qa' | 'revise';
    gpc_chat_session_id?: string;
    gpc_input?: Record<string, string | number | boolean>;
    gpc_handoff_id?: string;
    page_context?: Record<string, string | number>;
  } = { message };
  if (clientRequestId) body.client_request_id = clientRequestId;
  if (finalQuestion !== undefined) body.final_question = finalQuestion;
  if (regime !== undefined) body.regime = regime;
  if (options?.focusToken !== undefined) body.focus_token = options.focusToken;
  if (options?.topicFocus !== undefined) body.topic_focus = options.topicFocus;
  if (options?.priorClarificationQuestions !== undefined) {
    body.prior_clarification_questions = options.priorClarificationQuestions;
  }
  if (turnOptions?.gpcConfirmation !== undefined) {
    body.gpc_confirmation = turnOptions.gpcConfirmation;
  }
  if (turnOptions?.gpcChatSessionId !== undefined) {
    body.gpc_chat_session_id = turnOptions.gpcChatSessionId;
  }
  if (turnOptions?.gpcInput !== undefined) {
    body.gpc_input = turnOptions.gpcInput;
  }
  if (turnOptions?.gpcHandoffId !== undefined) {
    body.gpc_handoff_id = turnOptions.gpcHandoffId;
  }
  if (turnOptions?.pageContext !== undefined) {
    body.page_context = pageContextBody(turnOptions.pageContext);
  }
  const resp = await apiFetch(sessionUrl(sessionId, '/v2/turn'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    let body: unknown;
    let text = '';
    try {
      body = await resp.json();
      text = typeof body === 'string' ? body : JSON.stringify(body);
    } catch {
      try {
        text = await resp.text();
      } catch {
        text = '';
      }
    }
    throw new HomeChatHttpError(
      `HTTP ${resp.status} ${text}`.trim(),
      resp.status,
      body
    );
  }
  const raw = (await resp.json()) as
    | RawV2Turn
    | ClarificationEnvelope
    | TopicClarificationEnvelope
    | Record<string, unknown>;
  const gpcResponse = extractGpcRouterResponse(raw);
  if (gpcResponse) return withResponseLlmResultEvents(raw, gpcResponse);
  const mrResponse = extractMrRouterResponse(raw);
  if (mrResponse) return withResponseLlmResultEvents(raw, mrResponse);
  const constructionSowResponse = extractConstructionSowRouterResponse(raw);
  if (constructionSowResponse) return withResponseLlmResultEvents(raw, constructionSowResponse);
  const servicesPwsResponse = extractServicesPwsRouterResponse(raw);
  if (servicesPwsResponse) return withResponseLlmResultEvents(raw, servicesPwsResponse);
  const directPolicyResponse = extractDirectPolicyResponse(raw);
  if (directPolicyResponse) return withResponseLlmResultEvents(raw, directPolicyResponse);
  const cardResponse = extractHomeChatCardResponse(raw);
  if (cardResponse && !('turn_id' in raw)) return withResponseLlmResultEvents(raw, cardResponse);
  const status = (raw as { status?: string })?.status;
  if (status === 'clarification_needed') {
    return withResponseLlmResultEvents(raw, raw as ClarificationEnvelope);
  }
  if (status === 'topic_clarification_needed') {
    return withResponseLlmResultEvents(raw, normalizeTopicClarificationEnvelope(
      raw as Partial<TopicClarificationEnvelope> & Record<string, unknown>
    ));
  }
  if (isFastAnswerRecoveryEnvelope(raw)) {
    throw new FastAnswerRecoveryError(raw);
  }
  return withResponseLlmResultEvents(raw, {
    ...normalizeV2Turn(raw as RawV2Turn),
    ...(cardResponse ? { chat_card: cardResponse.chat_card } : {}),
  });
}

/**
 * Lazy-create + v2 turn in a single round-trip.
 *
 * POSTs to ``/api/home-chat/sessions/new/v2/turn``. The backend mints a
 * fresh session id AND runs the v2 multi-part pipeline against it,
 * returning ``{session_id, turn: <v2 Turn shape>}``. We unwrap the
 * envelope into a normalized ``{ session_id, turn: Turn }`` so the panel
 * can adopt the new id and append the rendered Turn in one step.
 *
 * Throws :class:`HomeChatHttpError` on non-2xx so the UI can render a
 * user-safe error without issuing a legacy v1 retry.
 */
export async function postLazyCreateV2Turn(
  message: string,
  clientRequestId?: string,
  finalQuestion?: string,
  regime?: FarRegime,
  options?: PostV2TurnOptions,
  turnOptions?: HomeChatTurnOptions
): Promise<
  | { session_id: string; turn: Turn }
  | ClarificationEnvelope
  | TopicClarificationEnvelope
  | GpcRouterResponse
  | MrChatSessionView
  | ConstructionSowChatSessionView
  | ServicesPwsChatSessionView
  | ClassifiedCuiUploadResponse
  | HomeChatCardResponse
> {
  // `final_question` resolves a clarification (skips the gate). On the
  // lazy-create path the `clarification_needed` envelope also carries the
  // freshly-minted `session_id`, so the resolving resubmit can target the
  // real session instead of re-minting one. Never forward a stale
  // `final_question` (clarification-step spec §4.1). `regime` carries the
  // user's chosen FAR Version on the resolve handoff (legacy-far-readers §4.1).
  // `focus_token` resolves a post-reader topic-focus round-trip; the
  // topic-focus envelope likewise carries `session_id` on the lazy-create path.
  const body: {
    message: string;
    client_request_id?: string;
    final_question?: string;
    regime?: FarRegime;
    focus_token?: string;
    topic_focus?: string;
    prior_clarification_questions?: string[];
    gpc_confirmation?: 'confirmed' | 'far_qa' | 'revise';
    gpc_chat_session_id?: string;
    gpc_input?: Record<string, string | number | boolean>;
    gpc_handoff_id?: string;
    page_context?: Record<string, string | number>;
  } = { message };
  if (clientRequestId) body.client_request_id = clientRequestId;
  if (finalQuestion !== undefined) body.final_question = finalQuestion;
  if (regime !== undefined) body.regime = regime;
  if (options?.focusToken !== undefined) body.focus_token = options.focusToken;
  if (options?.topicFocus !== undefined) body.topic_focus = options.topicFocus;
  if (options?.priorClarificationQuestions !== undefined) {
    body.prior_clarification_questions = options.priorClarificationQuestions;
  }
  if (turnOptions?.gpcConfirmation !== undefined) {
    body.gpc_confirmation = turnOptions.gpcConfirmation;
  }
  if (turnOptions?.gpcChatSessionId !== undefined) {
    body.gpc_chat_session_id = turnOptions.gpcChatSessionId;
  }
  if (turnOptions?.gpcInput !== undefined) {
    body.gpc_input = turnOptions.gpcInput;
  }
  if (turnOptions?.gpcHandoffId !== undefined) {
    body.gpc_handoff_id = turnOptions.gpcHandoffId;
  }
  if (turnOptions?.pageContext !== undefined) {
    body.page_context = pageContextBody(turnOptions.pageContext);
  }
  const resp = await apiFetch('/api/home-chat/sessions/new/v2/turn', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    let body: unknown;
    let text = '';
    try {
      body = await resp.json();
      text = typeof body === 'string' ? body : JSON.stringify(body);
    } catch {
      try {
        text = await resp.text();
      } catch {
        text = '';
      }
    }
    throw new HomeChatHttpError(
      `HTTP ${resp.status} ${text}`.trim(),
      resp.status,
      body
    );
  }
  const raw = (await resp.json()) as
    | { session_id: string; turn: RawV2Turn }
    | ClarificationEnvelope
    | TopicClarificationEnvelope
    | Record<string, unknown>;
  const gpcResponse = extractGpcRouterResponse(raw);
  if (gpcResponse) return withResponseLlmResultEvents(raw, gpcResponse);
  const mrResponse = extractMrRouterResponse(raw);
  if (mrResponse) return withResponseLlmResultEvents(raw, mrResponse);
  // The clear first-message generation route creates/binds the owner-scoped
  // Construction SOW controller in this very response. Parse it before the
  // legacy `{ session_id, turn }` fallback so a successful controller
  // envelope cannot be normalized as an undefined Turn and shown as a
  // generic "Failed to send message" error.
  const constructionSowResponse = extractConstructionSowRouterResponse(raw);
  if (constructionSowResponse) return withResponseLlmResultEvents(raw, constructionSowResponse);
  const servicesPwsResponse = extractServicesPwsRouterResponse(raw);
  if (servicesPwsResponse) return withResponseLlmResultEvents(raw, servicesPwsResponse);
  const directPolicyResponse = extractDirectPolicyResponse(raw);
  if (directPolicyResponse) return withResponseLlmResultEvents(raw, directPolicyResponse);
  const cardResponse = extractHomeChatCardResponse(raw);
  if (cardResponse && !('turn' in raw)) return withResponseLlmResultEvents(raw, cardResponse);
  const status = (raw as { status?: string })?.status;
  if (status === 'clarification_needed') {
    return withResponseLlmResultEvents(raw, raw as ClarificationEnvelope);
  }
  if (status === 'topic_clarification_needed') {
    return withResponseLlmResultEvents(raw, normalizeTopicClarificationEnvelope(
      raw as Partial<TopicClarificationEnvelope> & Record<string, unknown>
    ));
  }
  if (isFastAnswerRecoveryEnvelope(raw)) {
    throw new FastAnswerRecoveryError(raw);
  }
  const answered = raw as { session_id: string; turn: RawV2Turn };
  return withResponseLlmResultEvents(raw, {
    session_id: answered.session_id,
    turn: normalizeV2Turn(answered.turn),
    ...(cardResponse ? { chat_card: cardResponse.chat_card } : {}),
  });
}

export async function renameSession(
  sessionId: string,
  title: string
): Promise<void> {
  const resp = await apiFetch(sessionUrl(sessionId), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
  });
  await assertOk(resp);
}

export async function deleteSession(sessionId: string): Promise<void> {
  const resp = await apiFetch(sessionUrl(sessionId), { method: 'DELETE' });
  await assertOk(resp);
}

export async function listAttachments(sessionId: string): Promise<Attachment[]> {
  const resp = await apiFetch(sessionUrl(sessionId, '/attachments'), {
    method: 'GET',
  });
  await assertOk(resp);
  const body = await resp.json();
  if (Array.isArray(body)) return body as Attachment[];
  if (body && Array.isArray(body.attachments)) return body.attachments as Attachment[];
  return [];
}

export async function uploadAttachment(
  sessionId: string,
  file: File,
  options?: { signal?: AbortSignal }
): Promise<Attachment> {
  const form = new FormData();
  form.append('file', file);
  // Do NOT set Content-Type — the browser sets the multipart boundary.
  const init: RequestInit = {
    method: 'POST',
    body: form,
  };
  if (options?.signal !== undefined) {
    init.signal = options.signal;
  }
  const resp = await apiFetch(sessionUrl(sessionId, '/attachments'), init);
  await assertOk(resp);
  return (await resp.json()) as Attachment;
}

export async function deleteAttachment(
  sessionId: string,
  filename: string,
  options?: { signal?: AbortSignal }
): Promise<void> {
  const init: RequestInit = { method: 'DELETE' };
  if (options?.signal !== undefined) {
    init.signal = options.signal;
  }
  const resp = await apiFetch(
    `${sessionUrl(sessionId, '/attachments')}/${encodeURIComponent(filename)}`,
    init
  );
  await assertOk(resp);
}
